/**
 * Ask the graph.
 *
 * Every answer cites the records and documents it used, and a citation is a
 * link — clicking [2] opens the record it came from. When the graph holds no
 * evidence the answer is a refusal, shown as plainly as any other answer,
 * because a confident guess is the one failure mode this cannot have.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { askStream } from '../api/ask.stream'
import type { AskSource } from '../types/brain.types'

interface Turn {
  role: 'user' | 'bot'
  text: string
  citations?: AskSource[]
  sources?: AskSource[]
  streaming?: boolean
}

export function AskDrawer({ graphId }: { graphId: string }) {
  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [turns, setTurns] = useState<Turn[]>([])
  const [busy, setBusy] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight })
  }, [turns])

  async function send() {
    const text = question.trim()
    if (!text || busy) return

    setQuestion('')
    setBusy(true)
    setTurns((list) => [
      ...list,
      { role: 'user', text },
      { role: 'bot', text: '', streaming: true },
    ])

    const update = (patch: Partial<Turn>) =>
      setTurns((list) => {
        const next = [...list]
        const last = next[next.length - 1]
        next[next.length - 1] = { ...last, ...patch }
        return next
      })

    try {
      await askStream(graphId, text, {
        onContext: ({ sources }) => update({ sources }),
        onToken: (chunk) =>
          setTurns((list) => {
            const next = [...list]
            const last = next[next.length - 1]
            next[next.length - 1] = { ...last, text: last.text + chunk }
            return next
          }),
        onCitations: (citations) => update({ citations }),
        onError: (message) => update({ text: message }),
        onDone: () => update({ streaming: false }),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button className="askfab" onClick={() => setOpen(true)}>
        ✦ Ask
      </button>

      <div className="askdrawer" data-open={open} aria-hidden={!open}>
        <div className="askhead">
          <b>✦ Ask the graph</b>
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="askbody" ref={bodyRef}>
          {turns.length === 0 && (
            <div className="askmsg" data-role="bot">
              Ask anything about the data you have connected. Every answer cites the records it
              used — and when the graph holds no evidence, it says so rather than guessing.
            </div>
          )}

          {turns.map((turn, index) => (
            <div key={index} className="askmsg" data-role={turn.role}>
              {turn.role === 'bot' ? (
                <>
                  <Answer
                    text={turn.text || (turn.streaming ? 'Searching the graph…' : '')}
                    citations={turn.citations ?? []}
                    onCite={(source) => {
                      if (source.kind === 'record') {
                        setOpen(false)
                        navigate(`/brain/${graphId}/records/${source.ref_id}`)
                      }
                    }}
                  />
                  {turn.citations && turn.citations.length > 0 && (
                    <div className="small faint mono" style={{ marginTop: 6 }}>
                      {turn.citations.map((c) => `[${c.index}] ${c.title}`).join('  ')}
                    </div>
                  )}
                </>
              ) : (
                turn.text
              )}
            </div>
          ))}
        </div>

        <div className="askinput">
          <input
            className="input"
            value={question}
            placeholder="Which accounts have an open risk and an active deal?"
            aria-label="Ask a question"
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void send()
            }}
          />
          <button className="btn btn-primary" onClick={() => void send()} disabled={busy}>
            {busy ? '…' : '→'}
          </button>
        </div>
      </div>
    </>
  )
}

/** Renders [1]-style markers as buttons that jump to what they cite. */
function Answer({
  text,
  citations,
  onCite,
}: {
  text: string
  citations: AskSource[]
  onCite: (source: AskSource) => void
}) {
  const parts = text.split(/(\[\d{1,2}\])/g)
  return (
    <span>
      {parts.map((part, index) => {
        const match = /^\[(\d{1,2})\]$/.exec(part)
        if (!match) return <span key={index}>{part}</span>
        const source = citations.find((c) => c.index === Number(match[1]))
        if (!source) return <span key={index}>{part}</span>
        return (
          <button
            key={index}
            className="cite"
            title={`${source.title} — ${source.excerpt}`}
            onClick={() => onCite(source)}
          >
            {match[1]}
          </button>
        )
      })}
    </span>
  )
}
